import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
  
    // Check active sessions and sets the user
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email.split('@')[0],
          area: session.user.user_metadata?.area || 'Psicólogos',
          hasPaymentMethod: session.user.user_metadata?.hasPaymentMethod || false,
          isAdmin: session.user.user_metadata?.is_admin || false,
          occMemberNumberSubmitted: session.user.user_metadata?.occ_member_number_submitted || false,
          email_notifications: session.user.user_metadata?.email_notifications ?? true,
          instant_lead_notifications: session.user.user_metadata?.instant_lead_notifications ?? false,
          nif: session.user.user_metadata?.nif || null,
        });
      }
      setLoading(false);
    }).catch(() => {
      // Ignora erros de abort
      if (isMounted) setLoading(false);
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!isMounted) return;

      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.name || session.user.email.split('@')[0],
          area: session.user.user_metadata?.area || 'Psicólogos',
          hasPaymentMethod: session.user.user_metadata?.hasPaymentMethod || false,
          isAdmin: session.user.user_metadata?.is_admin || false,
          occMemberNumberSubmitted: session.user.user_metadata?.occ_member_number_submitted || false,
          email_notifications: session.user.user_metadata?.email_notifications ?? true,
          instant_lead_notifications: session.user.user_metadata?.instant_lead_notifications ?? false,
          nif: session.user.user_metadata?.nif || null,
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });
  
    return () => {
      isMounted = false;
      subscription?.unsubscribe();
    };
  }, []);

  const register = async (email, password, name, area, phone) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            area,
            phone,
            hasPaymentMethod: false
          }
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  };

  const login = async (email, password) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      localStorage.removeItem('leadflow_leads');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};